import { onUnmounted, reactive, ref } from "vue"

/**
 * Composable for managing submit button cooldown after errors
 * @param {number} duration - Cooldown duration in milliseconds (default: 30000)
 * @returns {Object} - Object containing cooldown state and control functions
 */
const useCooldown = (duration = 30000) => {
  const state = reactive({
    isCooldownActive: false,
    remainingSeconds: 0
  })

  const cooldownTimer = ref(null)
  const intervalTimer = ref(null)
  const startTime = ref(null)

  /**
   * Starts the cooldown timer
   * Clears any existing timer before starting a new one
   */
  const startCooldown = () => {
    clearCooldown() // Clear any existing timer
    state.isCooldownActive = true
    state.remainingSeconds = Math.ceil(duration / 1000)
    startTime.value = Date.now()

    intervalTimer.value = setInterval(() => {
      const elapsed = Date.now() - startTime.value
      const remaining = Math.max(0, Math.ceil((duration - elapsed) / 1000))
      state.remainingSeconds = remaining

      if (remaining <= 0) {
        clearInterval(intervalTimer.value)
        intervalTimer.value = null
      }
    }, 1000)

    cooldownTimer.value = setTimeout(() => {
      state.isCooldownActive = false
      state.remainingSeconds = 0
      cooldownTimer.value = null
      if (intervalTimer.value) {
        clearInterval(intervalTimer.value)
        intervalTimer.value = null
      }
    }, duration)
  }

  /**
   * Clears the cooldown timer and resets the state
   */
  const clearCooldown = () => {
    if (cooldownTimer.value) {
      clearTimeout(cooldownTimer.value)
      cooldownTimer.value = null
    }
    if (intervalTimer.value) {
      clearInterval(intervalTimer.value)
      intervalTimer.value = null
    }
    state.isCooldownActive = false
    state.remainingSeconds = 0
    startTime.value = null
  }

  /**
   * Cleanup function to be called on component unmount
   */
  onUnmounted(() => {
    clearCooldown()
  })

  return {
    state,
    startCooldown,
    clearCooldown
  }
}

export default useCooldown
